/**
 * OKX 支付模态框组件 - Admin Web
 * 
 * 显示 OKX 支付二维码和充值地址
 */

import { useState, useEffect } from 'react'
import okxService, { OKXPaymentOrder } from '@/services/okxService'
import { XMarkIcon, CheckCircleIcon, ClockIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline'

export interface OKXPaymentModalProps {
  isOpen: boolean
  onClose: () => void
  paymentOrder: OKXPaymentOrder | null
  onPaymentSuccess?: () => void
}

export default function OKXPaymentModal({
  isOpen,
  onClose,
  paymentOrder,
  onPaymentSuccess
}: OKXPaymentModalProps) {
  const [status, setStatus] = useState<string>('pending')
  const [timeRemaining, setTimeRemaining] = useState<number>(0)
  const [copied, setCopied] = useState(false)

  // 轮询支付状态
  useEffect(() => {
    if (!isOpen || !paymentOrder || status !== 'pending') {
      return
    }

    const pollInterval = setInterval(async () => {
      try {
        const result = await okxService.getOKXPaymentStatus(paymentOrder.orderId)
        
        if (result.success && result.data) {
          setStatus(result.data.status)
          
          if (result.data.status === 'paid') {
            clearInterval(pollInterval)
            onPaymentSuccess?.()
            
            // 3秒后自动关闭
            setTimeout(() => {
              onClose()
            }, 3000)
          } else if (result.data.status === 'expired' || result.data.status === 'failed') {
            clearInterval(pollInterval)
          }
        }
      } catch (error) {
        console.error('Error polling payment status:', error)
      }
    }, 3000) // 每3秒轮询一次

    return () => {
      clearInterval(pollInterval)
    }
  }, [isOpen, paymentOrder, status, onPaymentSuccess, onClose])

  // 倒计时
  useEffect(() => {
    if (!isOpen || !paymentOrder) {
      return
    }

    const updateTimeRemaining = () => {
      const expiresAt = new Date(paymentOrder.expiresAt).getTime()
      const now = Date.now()
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000))
      setTimeRemaining(remaining)

      if (remaining === 0 && status === 'pending') {
        setStatus('expired')
      }
    }

    updateTimeRemaining()
    const timer = setInterval(updateTimeRemaining, 1000)

    return () => clearInterval(timer)
  }, [isOpen, paymentOrder, status])

  // 复制地址到剪贴板
  const handleCopyAddress = async () => {
    if (!paymentOrder?.depositAddress) return

    try {
      await navigator.clipboard.writeText(paymentOrder.depositAddress)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  // 格式化时间
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  if (!isOpen || !paymentOrder) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">OKX 充值</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Status */}
          {status === 'paid' && (
            <div className="flex items-center justify-center space-x-2 text-green-600 bg-green-50 p-3 rounded-lg">
              <CheckCircleIcon className="w-5 h-5" />
              <span className="font-medium text-sm">支付成功！</span>
            </div>
          )}

          {status === 'expired' && (
            <div className="flex items-center justify-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
              <ClockIcon className="w-5 h-5" />
              <span className="font-medium text-sm">订单已过期</span>
            </div>
          )}

          {status === 'pending' && (
            <>
              {/* Timer and Amount - Combined */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-gray-600 mb-0.5">充值金额</div>
                    <div className="text-xl font-bold text-gray-900">
                      {paymentOrder.amount} {paymentOrder.currency}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {paymentOrder.chain}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-600 mb-0.5">剩余时间</div>
                    <div className="text-2xl font-bold text-blue-600">
                      {formatTime(timeRemaining)}
                    </div>
                  </div>
                </div>
              </div>

              {/* QR Code */}
              {paymentOrder.qrCode && (
                <div className="flex justify-center py-2">
                  <div className="bg-white p-2 rounded-lg border-2 border-gray-200">
                    <img
                      src={paymentOrder.qrCode}
                      alt="Payment QR Code"
                      className="w-48 h-48"
                    />
                  </div>
                </div>
              )}

              {/* Deposit Address */}
              <div>
                <div className="text-xs font-medium text-gray-700 mb-1.5">充值地址</div>
                <div className="flex items-center space-x-1.5">
                  <input
                    type="text"
                    value={paymentOrder.depositAddress}
                    readOnly
                    className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded bg-gray-50 text-xs font-mono"
                  />
                  <button
                    onClick={handleCopyAddress}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center space-x-1"
                  >
                    <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                    <span className="text-xs">{copied ? '已复制' : '复制'}</span>
                  </button>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2.5">
                <div className="text-xs text-yellow-800 space-y-1">
                  <p className="font-medium">充值说明：</p>
                  <ul className="list-disc list-inside space-y-0.5 text-xs">
                    <li>使用 OKX 钱包扫码或复制地址转账</li>
                    <li>仅支持 {paymentOrder.chain} 网络</li>
                    <li>到账时间约 1-10 分钟</li>
                  </ul>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm"
          >
            {status === 'paid' ? '完成' : '关闭'}
          </button>
        </div>
      </div>
    </div>
  )
}
